import JSZip from 'jszip';
import * as pdfjsLib from 'pdfjs-dist';
import BaseConverter from './BaseConverter.js';
import pdfCss from '../../styles/pdf-conversion.css?raw';
import pdfWorkerUrl from '../../assets/workers/pdf.worker.min.js?url';

// Set up PDF.js worker using bundled asset
if (typeof window !== 'undefined') {
  pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
  console.log('PDF.js worker configured with bundled asset:', pdfWorkerUrl);
  console.log('PDF.js version:', pdfjsLib.version);
}

/**
 * Converter for PDF files to EPUB format
 */
class PdfConverter extends BaseConverter {
  /**
   * Check if a file is a PDF file
   */
  isPdfFile(fileName) {
    return fileName.toLowerCase().endsWith('.pdf');
  }

  /**
   * Convert PDF file to EPUB
   */
  async convertToEpub(pdfFile, options, onFileProgress) {
    try {
      console.log('=== PDF CONVERSION START ===');
      console.log('Starting PDF to EPUB conversion (browser-only approach) for:', pdfFile.name);
      console.log('File size:', pdfFile.size, 'bytes');
      console.log('File type:', pdfFile.type);
      console.log('PDF.js worker src:', pdfjsLib.GlobalWorkerOptions.workerSrc);
      
      if (onFileProgress) onFileProgress(10);

      // Step 1: Parse PDF and extract content using pdf.js
      console.log('Loading PDF document with pdf.js...');
      const arrayBuffer = await pdfFile.arrayBuffer();
      console.log('ArrayBuffer loaded, size:', arrayBuffer.byteLength);
      
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      console.log('PDF loaded successfully. Pages:', pdf.numPages);
      
      if (onFileProgress) onFileProgress(20);

      // Step 2: Render each PDF page as an image
      console.log('Rendering PDF pages as images...');
      const chapters = [];
      const totalPages = pdf.numPages;
      
      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        
        // Render the entire page to canvas to capture all visual content
        const pageImages = [];
        
        try {
          // Render the entire page to canvas to capture all visual content
          const viewport = page.getViewport({ scale: 1.5 }); // Higher scale for better quality
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          
          // Render the page
          const renderContext = {
            canvasContext: ctx,
            viewport: viewport
          };
          
          await page.render(renderContext).promise;
          
          // Convert the rendered page to a JPEG image
          const pageImageBlob = await new Promise(resolve => {
            canvas.toBlob(resolve, 'image/jpeg', 0.8);
          });
          
          if (pageImageBlob && pageImageBlob.size > 1000) { // Only include if substantial size
            pageImages.push({
              name: `page${pageNum}_rendered.jpg`,
              blob: pageImageBlob,
              width: canvas.width,
              height: canvas.height
            });
            console.log(`Rendered page ${pageNum} as image: ${canvas.width}x${canvas.height}`);
          }
          
        } catch (renderError) {
          console.warn(`Failed to render page ${pageNum} as image:`, renderError.message);
        }
        
        // Create chapter with only images (no text duplication)
        if (pageImages.length > 0) {
          chapters.push({
            title: `Page ${pageNum}`,
            content: '', // No text content since it's already in the image
            images: pageImages,
            pageNumber: pageNum
          });
        }
        
        // Update progress for page rendering
        const renderProgress = 20 + (pageNum / totalPages) * 50;
        if (onFileProgress) onFileProgress(renderProgress);
      }

      console.log(`Extracted ${chapters.length} chapters from PDF`);
      if (onFileProgress) onFileProgress(75);

      // Step 3: Build EPUB structure in memory using JSZip
      console.log('Building EPUB structure...');
      const zip = new JSZip();
      const bookTitle = pdfFile.name.replace('.pdf', '');
      const uuid = this.generateUUID();
      
      // EPUB structure: mimetype (uncompressed)
      zip.file("mimetype", "application/epub+zip", { compression: "STORE" });
      
      // META-INF/container.xml
      const containerXml = `<?xml version="1.0" encoding="UTF-8"?>
<container version="1.0" xmlns="urn:oasis:names:tc:opendocument:xmlns:container">
  <rootfiles>
    <rootfile full-path="OEBPS/content.opf" media-type="application/oebps-package+xml"/>
  </rootfiles>
</container>`;
      zip.file("META-INF/container.xml", containerXml);
      
      // OEBPS/content.opf (package document)
      const chapterManifest = chapters.map((_, index) => 
        `    <item id="chapter${index + 1}" href="chapter${index + 1}.xhtml" media-type="application/xhtml+xml"/>`
      ).join('\n');
      
      // Add images to manifest
      const imageManifest = [];
      chapters.forEach((chapter, chapterIndex) => {
        chapter.images.forEach(image => {
          imageManifest.push(`    <item id="${image.name.replace('.jpg', '')}" href="images/${image.name}" media-type="image/jpeg"/>`);
        });
      });
      
      const manifest = chapterManifest + (imageManifest.length > 0 ? '\n' + imageManifest.join('\n') : '');
      
      const spine = chapters.map((_, index) => 
        `    <itemref idref="chapter${index + 1}"/>`
      ).join('\n');

      const contentOpf = `<?xml version="1.0" encoding="UTF-8"?>
<package version="2.0" xmlns="http://www.idpf.org/2007/opf" unique-identifier="BookId">
  <metadata xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:opf="http://www.idpf.org/2007/opf">
    <dc:identifier id="BookId" opf:scheme="UUID">${uuid}</dc:identifier>
    <dc:title>${bookTitle}</dc:title>
    <dc:creator opf:role="aut">PDF Converter</dc:creator>
    <dc:language>en</dc:language>
    <dc:date opf:event="modification">${new Date().toISOString().split('T')[0]}</dc:date>
    <dc:publisher>FileConvertHub</dc:publisher>
    <dc:description>Converted from PDF: ${pdfFile.name}</dc:description>
  </metadata>
  <manifest>
    <item id="ncx" href="toc.ncx" media-type="application/x-dtbncx+xml"/>
    <item id="stylesheet" href="stylesheet.css" media-type="text/css"/>
${manifest}
  </manifest>
  <spine toc="ncx">
${spine}
  </spine>
</package>`;
      zip.file("OEBPS/content.opf", contentOpf);
      
      // OEBPS/toc.ncx (navigation)
      const navPoints = chapters.map((chapter, index) => `
    <navPoint id="navpoint-${index + 1}" playOrder="${index + 1}">
      <navLabel>
        <text>${chapter.title}</text>
      </navLabel>
      <content src="chapter${index + 1}.xhtml"/>
    </navPoint>`).join('');

      const tocNcx = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE ncx PUBLIC "-//NISO//DTD ncx 2005-1//EN" "http://www.daisy.org/z3986/2005/ncx-2005-1.dtd">
<ncx xmlns="http://www.daisy.org/z3986/2005/ncx/" version="2005-1">
  <head>
    <meta name="dtb:uid" content="${uuid}"/>
    <meta name="dtb:depth" content="1"/>
    <meta name="dtb:totalPageCount" content="0"/>
    <meta name="dtb:maxPageNumber" content="0"/>
  </head>
  <docTitle>
    <text>${bookTitle}</text>
  </docTitle>
  <navMap>${navPoints}
  </navMap>
</ncx>`;
      zip.file("OEBPS/toc.ncx", tocNcx);
      
      // OEBPS/stylesheet.css (optimized for image-only content)
      zip.file("OEBPS/stylesheet.css", pdfCss);
      
      // Add images to OEBPS/images/ folder
      chapters.forEach((chapter) => {
        chapter.images.forEach(image => {
          zip.file(`OEBPS/images/${image.name}`, image.blob);
        });
      });
      
      // OEBPS/chapterX.xhtml (one file per page, image-only)
      chapters.forEach((chapter, index) => {
        const xhtmlContent = `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE html PUBLIC "-//W3C//DTD XHTML 1.1//EN" "http://www.w3.org/TR/xhtml11/DTD/xhtml11.dtd">
<html xmlns="http://www.w3.org/1999/xhtml">
<head>
  <title>${chapter.title}</title>
  <link rel="stylesheet" type="text/css" href="stylesheet.css"/>
</head>
<body>
  <div class="chapter-title">${chapter.title}</div>
  ${this.formatImagesAsHtml(chapter.images)}
</body>
</html>`;
        zip.file(`OEBPS/chapter${index + 1}.xhtml`, xhtmlContent);
      });

      if (onFileProgress) onFileProgress(90);

      // Step 4: Generate ZIP in browser
      console.log('Generating EPUB file...');
      const epubBlob = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: { level: options.compressionLevel }
      });

      if (onFileProgress) onFileProgress(100);
      console.log('PDF to EPUB conversion completed successfully');
      console.log('Generated EPUB blob size:', epubBlob.size, 'bytes');
      console.log('Generated EPUB blob type:', epubBlob.type);
      console.log('=== PDF CONVERSION END ===');
      
      return epubBlob;
      
    } catch (error) {
      console.error('Error converting PDF to EPUB:', error);
      console.error('Error details:', error.message, error.stack);
      throw new Error(`Failed to convert PDF: ${error.message}`);
    }
  }

  /**
   * Format images array as HTML img tags
   */
  formatImagesAsHtml(images) {
    if (!images || images.length === 0) {
      return '';
    }
    
    return images.map(image => 
      `<div class="image-container">
        <img src="images/${image.name}" alt="Page image" class="page-image" width="${Math.min(image.width, 600)}" height="auto"/>
      </div>`
    ).join('\n');
  }
}

export default PdfConverter;
